import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "@phosphor-icons/react";
import { SERVICES } from "@/seoData";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Photo = ({ src, label, alt }) => (
  <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-[#F4F4F5]">
    <img src={`${BACKEND}${src}?w=640`} srcSet={`${BACKEND}${src}?w=480 480w, ${BACKEND}${src}?w=640 640w, ${BACKEND}${src}?w=960 960w`} sizes="(max-width: 640px) 50vw, 400px" alt={alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
    <span className={`absolute top-2 left-2 text-[11px] font-display font-extrabold uppercase tracking-wider px-2 py-1 rounded-sm text-white ${label === "Before" ? "bg-[#15306E]" : "bg-[#46A01E]"}`}>{label}</span>
  </div>
);

export const QuoteLikeThis = ({ id }) => (
  <Link to="/#estimate" data-testid={`quote-like-this-${id}`}
    className="inline-flex items-center gap-1.5 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-xs px-4 py-2.5 rounded-sm transition-colors">
    Get a quote for a job like this <ArrowRight size={14} weight="bold" />
  </Link>
);

const matchesPlace = (text, places) => {
  const t = (text || "").toLowerCase().trim();
  return t.length > 2 && places.some((p) => {
    const x = p.toLowerCase();
    return t.includes(x) || (t.length >= 4 && x.includes(t));
  });
};

export default function JobStories({ serviceSlug, serviceName, cityName, cityNeighborhoods = [] }) {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    const params = serviceSlug ? { service: serviceSlug } : {};
    axios.get(`${BACKEND}/api/job-stories`, { params })
      .then(({ data }) => setStories(cityName ? data.filter((s) => matchesPlace(s.neighborhood, [cityName, ...cityNeighborhoods])) : data))
      .catch(() => setStories([]));
  }, [serviceSlug, cityName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (stories.length === 0) return null;
  const place = cityName || "San Diego";

  return (
    <section className="px-5 lg:px-8 py-16" data-testid="job-stories">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E]">
          Before &amp; after: recent jobs in {place}
        </h2>
        <p className="text-[#52525B] mt-2 mb-8">
          {cityName ? `Real work from our crew in ${cityName}, tagged by neighborhood.` : `Real ${serviceName.toLowerCase()} work from our crew, tagged by neighborhood.`}
        </p>
        <div className={`grid gap-6 ${stories.length > 1 ? "md:grid-cols-2" : "max-w-3xl"}`}>
          {stories.map((s) => {
            const name = (serviceName || SERVICES.find((x) => x.slug === s.service_slug)?.name || "Junk removal").toLowerCase();
            return (
              <figure key={s.id} data-testid={`job-story-${s.id}`} className="border border-[#E4E4E7] rounded-sm p-4 bg-white">
                <div className="grid grid-cols-2 gap-3">
                  <Photo src={s.before_url} label="Before" alt={`Before photo, ${name} job in ${s.neighborhood}, San Diego`} />
                  <Photo src={s.after_url} label="After" alt={`After photo, ${name} job in ${s.neighborhood}, San Diego`} />
                </div>
                <figcaption className="mt-3">
                  <span className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-[#15306E]">
                    <MapPin size={15} weight="fill" className="text-[#46A01E]" /> {s.neighborhood}
                  </span>
                  {s.caption && <p className="text-sm text-[#52525B] mt-1 leading-relaxed">{s.caption}</p>}
                  <div className="mt-3"><QuoteLikeThis id={s.id} /></div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
